import { ChangeEvent, useRef, useState } from "react"
import Api from "../api/api"
import { uploadFileWithProgress } from "../utils/upload"

// Mirrors SKU_NAME_REGEX in lib/api/skus.ts: a sku name needs at least one
// letter or digit.
export const skuNameValid = (name: string) => /[A-Za-z0-9]/.test(name)

// These mirror SKU_VERSION_REGEX / INSTALLER_FILENAME_PATTERNS /
// validateInstallerFileName in lib/api/skus.ts — the backend rejects
// violations too.
export const VERSION_REGEX = /^\d+\.\d+\.\d+$/
const INSTALLER_EXTENSIONS = {
  osx: ["dmg", "pkg", "zip"],
  windows: ["exe", "zip"]
}
const INSTALLER_FILENAME_PATTERNS = {
  osx: /-(\d+\.\d+\.\d+)-osx-installer\.(dmg|pkg|zip)$/,
  windows: /-(\d+\.\d+\.\d+)-windows-installer\.(exe|zip)$/
}
const INSTALLER_FILENAME_REGEX = /^[A-Za-z0-9][A-Za-z0-9._-]*$/

// Hard error (submit disabled) for a staged installer file, or null. Computed
// at render so editing the version field re-checks already-staged files.
function installerFileError(fileName: string, platform: "osx" | "windows", version: string): string | null {
  if (fileName.length > 200 || !INSTALLER_FILENAME_REGEX.test(fileName) || fileName.includes(".."))
    return "File name may only contain letters, digits, dots, dashes and underscores"
  const match = INSTALLER_FILENAME_PATTERNS[platform].exec(fileName)
  if (match === null)
    return `File name must end with -MAJOR.MINOR.PATCH-${platform}-installer plus ${INSTALLER_EXTENSIONS[platform].map(e => `.${e}`).join(" / ")}`
  if (version !== "" && match[1] !== version)
    return `File name version (${match[1]}) doesn't match the version field (${version})`
  return null
}

// The version embedded in a pattern-conforming installer file name, or null.
function versionFromInstallerFileName(fileName: string, platform: "osx" | "windows"): string | null {
  return INSTALLER_FILENAME_PATTERNS[platform].exec(fileName)?.[1] ?? null
}

const urlValid = (u: string) => u.trim() === "" || u.trim().startsWith("https://")

export interface InstallerFields {
  version: string
  setVersion: (v: string) => void
  macUrl: string
  setMacUrl: (u: string) => void
  winUrl: string
  setWinUrl: (u: string) => void
  macFile: File | null
  winFile: File | null
  pick: (platform: "osx" | "windows") => (e: ChangeEvent<HTMLInputElement>) => void
  clear: (platform: "osx" | "windows") => void
  uploadStatus: { label: string, percent: number } | null
  macFileError: string | null
  winFileError: string | null
  // any installer input at all: a staged file or a typed URL
  hasAny: boolean
  // installer inputs are consistent (URL shapes, file-name pattern/version
  // match, version present whenever hasAny)
  valid: boolean
  // presign + PUT every staged file for an existing sku (the server derives
  // the key from the sku's owner and id); returns the download URLs
  // (uploaded ones, falling back to the typed URLs)
  upload: (skuId: string) => Promise<{ downloadMacUrl?: string, downloadWinUrl?: string }>
  macFileInputRef: React.RefObject<HTMLInputElement | null>
  winFileInputRef: React.RefObject<HTMLInputElement | null>
}

// All installer-related state for a dialog: the version field, per-platform
// staged files / manual URLs, and the presign→PUT upload flow. Shared by
// AddSkuDialog (first version at sku creation) and AddVersionDialog (add and
// edit modes — `initial` seeds the fields with an existing version's values).
export function useInstallerFields(accessToken: string, initial?: { version?: string, macUrl?: string, winUrl?: string }): InstallerFields {
  const [version, setVersion] = useState(initial?.version ?? "")
  const [macUrl, setMacUrl] = useState(initial?.macUrl ?? "")
  const [winUrl, setWinUrl] = useState(initial?.winUrl ?? "")
  const [macFile, setMacFile] = useState<File | null>(null)
  const [winFile, setWinFile] = useState<File | null>(null)
  const [uploadStatus, setUploadStatus] = useState<{ label: string, percent: number } | null>(null)
  const macFileInputRef = useRef<HTMLInputElement>(null)
  const winFileInputRef = useRef<HTMLInputElement>(null)

  // Any file stages (errors are computed at render and disable submit); an
  // empty version field auto-fills from the first pattern-conforming pick.
  const pick = (platform: "osx" | "windows") => (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""   // allow re-picking the same file
    if (!file) return
    ;(platform === "osx" ? setMacFile : setWinFile)(file)
    if (version.trim() === "") {
      const fromName = versionFromInstallerFileName(file.name, platform)
      if (fromName !== null) setVersion(fromName)
    }
  }

  const clear = (platform: "osx" | "windows") =>
    (platform === "osx" ? setMacFile : setWinFile)(null)

  const macFileError = macFile ? installerFileError(macFile.name, "osx", version.trim()) : null
  const winFileError = winFile ? installerFileError(winFile.name, "windows", version.trim()) : null
  const hasAny = macFile !== null || winFile !== null || macUrl.trim() !== "" || winUrl.trim() !== ""
  const valid =
    (macFile !== null || urlValid(macUrl)) && (winFile !== null || urlValid(winUrl))
    && (!hasAny || VERSION_REGEX.test(version.trim()))
    && macFileError === null && winFileError === null

  const upload = async (skuId: string): Promise<{ downloadMacUrl?: string, downloadWinUrl?: string }> => {
    let uploadedMacUrl: string | undefined
    let uploadedWinUrl: string | undefined
    try {
      for (const [file, platform, label] of [[macFile, "osx", "macOS"], [winFile, "windows", "Windows"]] as const) {
        if (!file) continue
        const { uploadUrl, downloadUrl } = await Api.createSkuInstallerUpload({
          skuId, version: version.trim(), platform, fileName: file.name, accessToken
        })
        setUploadStatus({ label: `Uploading ${label} installer…`, percent: 0 })
        // the Content-Type is part of the presign signature
        await uploadFileWithProgress(uploadUrl, file, "application/octet-stream",
          f => setUploadStatus({ label: `Uploading ${label} installer…`, percent: Math.round(f * 100) }))
        if (platform === "osx") uploadedMacUrl = downloadUrl; else uploadedWinUrl = downloadUrl
      }
    } finally {
      setUploadStatus(null)
    }
    return {
      downloadMacUrl: uploadedMacUrl ?? (macUrl.trim() || undefined),
      downloadWinUrl: uploadedWinUrl ?? (winUrl.trim() || undefined)
    }
  }

  return {
    version, setVersion, macUrl, setMacUrl, winUrl, setWinUrl,
    macFile, winFile, pick, clear, uploadStatus,
    macFileError, winFileError, hasAny, valid, upload,
    macFileInputRef, winFileInputRef
  }
}

// Required-field marker for the sku/version dialog labels — oversized so it
// reads against the small uppercase grey labels; lineHeight 0 keeps it from
// stretching the label row.
export const requiredMark = (
  <span aria-hidden="true" style={{ color: "var(--nfa-orange)", marginLeft: 3, fontWeight: 700, fontSize: "1.4em", lineHeight: 0, verticalAlign: "-0.2em" }}>*</span>
)

// The version field plus the two per-platform installer fields (URL input or
// staged file with errors/hints) and the upload progress line. The version is
// always required; installersRequired controls the installer labels'
// asterisks only (Add SKU: installers are mandatory just for license claims —
// credits-only versions are installer-less; the Add Version wizard always
// requires them).
// versionError: a caller-side complaint about an otherwise well-formed
// version (e.g. the wizard's increment-only rule) — shown under the version
// field in place of the folder hint, at the hint's size so the row keeps its
// height.
// versionWarning: a non-blocking caution about a well-formed, accepted
// version (e.g. "above your draft") — yellow, shown only while versionError
// is clear (the error wins the slot).
// hideVersion: skip the version input entirely — for dialogs where the
// version number is fixed (the release/beta promotion dialogs seed the hook
// with it; the file-name-embeds-version validation still applies).
export function InstallerFieldsView({ fields, busy, installersRequired = true, versionError = null, versionWarning = null, hideVersion = false }: { fields: InstallerFields, busy: boolean, installersRequired?: boolean, versionError?: string | null, versionWarning?: string | null, hideVersion?: boolean }) {
  const f = fields
  const versionTrimmed = f.version.trim()
  const versionValid = VERSION_REGEX.test(versionTrimmed)
  return (
    <>
      {!hideVersion && (
        <label className="generate-codes-field">
          <span className="generate-codes-label">Version{requiredMark}</span>
          <input
            type="text"
            value={f.version}
            onChange={(e) => f.setVersion(e.target.value)}
            className="generate-codes-input"
            disabled={busy}
            placeholder="e.g. 1.14.4"
          />
          {versionTrimmed !== "" && !versionValid && (
            <span className="generate-codes-error">Version must be MAJOR.MINOR.PATCH</span>
          )}
          {versionTrimmed === "" && f.hasAny && (
            <span className="generate-codes-error">Version is required when providing installer files or URLs</span>
          )}
          {versionTrimmed === "" && !f.hasAny && (
            <span className="generate-codes-hint">Auto-filled from the first uploaded installer file name</span>
          )}
          {versionTrimmed !== "" && versionValid && versionError !== null ? (
            <span className="generate-codes-error">{versionError}</span>
          ) : versionTrimmed !== "" && versionValid && versionWarning !== null ? (
            <span style={{ color: "var(--nfa-yellow)", fontSize: "9pt" }}>{versionWarning}</span>
          ) : versionTrimmed !== "" && versionValid ? (
            <span className="generate-codes-hint">Folder for the uploaded installer files: …/installers/{versionTrimmed}/</span>
          ) : null}
        </label>
      )}

      {([
        { platform: "osx", label: "macOS installer", file: f.macFile, url: f.macUrl, setUrl: f.setMacUrl, error: f.macFileError, inputRef: f.macFileInputRef, accept: ".dmg,.pkg,.zip" },
        { platform: "windows", label: "Windows installer", file: f.winFile, url: f.winUrl, setUrl: f.setWinUrl, error: f.winFileError, inputRef: f.winFileInputRef, accept: ".exe,.zip" }
      ] as const).map(({ platform, label, file, url, setUrl, error, inputRef, accept }) => (
        <div key={platform} className="generate-codes-field">
          <span className="generate-codes-label">{label}{installersRequired && requiredMark}</span>
          {file ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <span>{file.name}</span>
                <span className="generate-codes-hint">{(file.size / 1048576).toFixed(1)} MB</span>
                <button
                  className="card-link"
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "10pt" }}
                  onClick={() => f.clear(platform)}
                  disabled={busy}
                >
                  Remove
                </button>
              </div>
              {versionTrimmed !== "" && versionValid && !error && (
                <span className="generate-codes-hint">
                  Uploads to: …/installers/{versionTrimmed}/…/{file.name} — every upload gets a fresh URL
                </span>
              )}
            </>
          ) : (
            <>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="generate-codes-input"
                disabled={busy}
                placeholder="https://…"
              />
              <button
                className="card-link"
                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                onClick={() => inputRef.current?.click()}
                disabled={busy}
              >
                …or upload a file
              </button>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            style={{ display: "none" }}
            onChange={f.pick(platform)}
          />
          {error && <span className="generate-codes-error">{error}</span>}
        </div>
      ))}

      {f.uploadStatus && (
        // deliberately louder than the grey hints — this is live progress the
        // user is waiting on, not fine print
        <p style={{ color: "var(--nfa-orange)", fontSize: "10pt", margin: 0 }}>
          {f.uploadStatus.label} {f.uploadStatus.percent}%
        </p>
      )}
    </>
  )
}
